// Pestaña "Cruce Alegra" — cargos bancarios vs egresos contables de Alegra.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { Loader2, RefreshCw, AlertTriangle, Zap, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const CAT_TIPO = { expense: 'Gasto', liability: 'Pasivo', asset: 'Activo', income: 'Ingreso', equity: 'Patrimonio' };

export const CruceAlegraTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [egDialog, setEgDialog] = useState({ open: false, cargo: null });
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(null);
  const [provQ, setProvQ] = useState('');
  const [provs, setProvs] = useState([]);
  const [provSel, setProvSel] = useState(null);
  const [egNota, setEgNota] = useState('');
  const [creando, setCreando] = useState(false);

  const load = useCallback(async (d, h) => {
    setLoading(true);
    try {
      const params = {};
      if (d) params.desde = d;
      if (h) params.hasta = h;
      const r = await api.get('/conciliacion-bancaria/cruce-alegra', { params });
      setData(r.data);
      if (r.data.periodo) {
        setDesde(r.data.periodo.desde);
        setHasta(r.data.periodo.hasta);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en cruce Alegra');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buscarProv = async (q) => {
    if (!q || q.trim().length < 2) { setProvs([]); return []; }
    try {
      const r = await api.get('/conciliacion-bancaria/proveedores-alegra', { params: { q: q.trim() } });
      const list = r.data.proveedores || [];
      setProvs(list);
      return list;
    } catch { setProvs([]); return []; }
  };

  const openEgreso = async (c) => {
    setEgDialog({ open: true, cargo: c });
    setCuentaSel(null); setProvSel(null); setProvs([]); setEgNota(''); setProvQ('');
    if (cuentas.length === 0) {
      try {
        const r = await api.get('/conciliacion-bancaria/cuentas-contables');
        setCuentas(r.data.cuentas || []);
      } catch { toast.error('Error cargando el plan de cuentas'); }
    }
    if (/LINEA DE CRED|INTERES|COMISION|IMPTO|IMPUESTO LINEA/i.test(c.detalle || '')) {
      setProvQ('BANCO DE CHILE');
      const list = await buscarProv('BANCO DE CHILE');
      const bch = list.find((p) => String(p.alegra_id) === '639');
      if (bch) setProvSel(bch);
    }
  };

  const confirmarEgreso = async () => {
    if (!cuentaSel || !provSel) { toast.error('Selecciona cuenta contable y proveedor'); return; }
    setCreando(true);
    try {
      const r = await api.post(`/conciliacion-bancaria/movimientos/${egDialog.cargo.id}/egreso-express`, {
        categoria_id: String(cuentaSel),
        proveedor_alegra_id: String(provSel.alegra_id),
        nota: egNota,
      });
      toast.success(r.data.message);
      setEgDialog({ open: false, cargo: null });
      load(desde, hasta);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando el egreso en Alegra');
    } finally { setCreando(false); }
  };

  if (loading && !data) return <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const r = data?.resumen;

  return (
    <div className="space-y-3" data-testid="cruce-alegra-tab">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-2 text-sm text-muted-foreground">
          Cruza los <b>cargos del banco</b> con los <b>egresos contables de Alegra</b> (monto exacto, fecha ±7 días).
        </p>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="cruce-desde" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="cruce-hasta" />
        <Button size="sm" variant="outline" onClick={() => load(desde, hasta)} data-testid="btn-cruce-actualizar">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Actualizar
        </Button>
      </div>

      {r && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="cruce-resumen">
          {[
            ['Cargos banco', r.cargos_banco, ''],
            ['Emparejados con Alegra', r.emparejados, 'text-emerald-600'],
            [`Cargos SIN egreso ($${CLP(r.monto_cargos_sin_egreso)})`, r.cargos_sin_egreso, 'text-red-600'],
            [`Egresos SIN banco ($${CLP(r.monto_egresos_sin_banco)})`, r.egresos_sin_banco, 'text-amber-600'],
          ].map(([label, val, cls]) => (
            <Card key={label}><CardContent className="p-3">
              <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
              <p className={`text-lg font-bold ${cls}`}>{val}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
              <AlertTriangle className="h-4 w-4" /> Cargos del banco SIN egreso en Alegra — usa ⚡ para crearlo exprés
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {(data?.cargos_sin_egreso || []).map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-3 py-1.5">{fmtFecha(c.fecha)}</td>
                      <td className="max-w-[200px] truncate px-3 py-1.5" title={c.detalle}>{c.detalle}</td>
                      <td className="px-3 py-1.5 text-[10px] text-muted-foreground">{c.match?.tipo || c.estado}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-red-600">${CLP(c.cargo)}</td>
                      <td className="whitespace-nowrap px-1 py-1.5 text-right">
                        {c.egreso_express_id ? (
                          <span className="text-[10px] text-emerald-600" title={`Egreso Alegra #${c.egreso_express_id}`}>✓ #{c.egreso_express_id}</span>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-sky-600" title="Crear egreso exprés en Alegra"
                            onClick={() => openEgreso(c)} data-testid={`btn-egreso-express-${c.id}`}>
                            <Zap className="mr-0.5 h-3 w-3" /> Egreso
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(data?.cargos_sin_egreso || []).length === 0 && (
                    <tr><td className="px-3 py-6 text-center text-muted-foreground">Todos los cargos tienen egreso contable ✓</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
              <AlertTriangle className="h-4 w-4" /> Egresos de Alegra SIN cargo bancario (revisar duplicados / otra cuenta)
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {(data?.egresos_sin_banco || []).map((e) => (
                    <tr key={e.alegra_id} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-3 py-1.5">{fmtFecha(e.fecha)}</td>
                      <td className="max-w-[200px] truncate px-3 py-1.5">{e.cliente_nombre || `Egreso #${e.alegra_id}`}</td>
                      <td className="max-w-[120px] truncate px-3 py-1.5 text-[10px] text-muted-foreground">{e.cuenta_bancaria || ''}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-amber-700">${CLP(Math.round(e.monto))}</td>
                    </tr>
                  ))}
                  {(data?.egresos_sin_banco || []).length === 0 && (
                    <tr><td className="px-3 py-6 text-center text-muted-foreground">Todos los egresos calzan con el banco ✓</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={egDialog.open} onOpenChange={(o) => !o && setEgDialog({ open: false, cargo: null })}>
        <DialogContent className="max-w-lg" data-testid="dialog-egreso-express">
          <DialogHeader><DialogTitle>⚡ Egreso exprés en Alegra</DialogTitle></DialogHeader>
          {egDialog.cargo && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="font-medium">{egDialog.cargo.detalle}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtFecha(egDialog.cargo.fecha)} · Cargo <b className="text-red-600">${CLP(egDialog.cargo.cargo)}</b>{egDialog.cargo.cuenta ? ` · cta ${egDialog.cargo.cuenta}` : ''}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium">Cuenta contable (plan de cuentas Alegra)</p>
                <SearchableCombobox
                  items={cuentas.map((c) => ({ value: c.id, label: c.name, subLabel: CAT_TIPO[c.type] || c.type }))}
                  value={cuentaSel}
                  onChange={(item) => setCuentaSel(item ? item.value : null)}
                  placeholder="Ej: Intereses de Préstamos Bancarios..."
                  testid="combobox-cuenta-contable"
                />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium">Proveedor / contacto Alegra</p>
                <div className="flex gap-1">
                  <Input value={provQ} onChange={(e) => setProvQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && buscarProv(provQ)}
                    placeholder="Buscar por nombre o RUT..." className="h-8 text-xs" data-testid="input-prov-egreso" />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => buscarProv(provQ)} data-testid="btn-buscar-prov-egreso">
                    Buscar
                  </Button>
                </div>
                {provs.length > 0 && (
                  <div className="mt-1 max-h-36 space-y-1 overflow-y-auto">
                    {provs.map((p) => (
                      <label key={p.alegra_id}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 text-xs ${provSel?.alegra_id === p.alegra_id ? 'border-sky-400 bg-sky-50' : ''}`}>
                        <input type="radio" name="prov-egreso" checked={provSel?.alegra_id === p.alegra_id}
                          onChange={() => setProvSel(p)} data-testid={`prov-radio-${p.alegra_id}`} />
                        <span className="flex-1 truncate">{p.nombre}</span>
                        <span className="text-[10px] text-muted-foreground">{p.identificacion}</span>
                      </label>
                    ))}
                  </div>
                )}
                {provSel && (
                  <p className="mt-1 text-xs text-emerald-700" data-testid="prov-egreso-sel">✓ {provSel.nombre}</p>
                )}
              </div>
              <Textarea value={egNota} onChange={(e) => setEgNota(e.target.value)} placeholder="Nota (opcional)" rows={2} data-testid="egreso-nota" />
              <p className="rounded bg-sky-50 px-2 py-1.5 text-[11px] text-sky-800">
                Se creará un egreso en Alegra por <b>${CLP(egDialog.cargo.cargo)}</b> con fecha {fmtFecha(egDialog.cargo.fecha)} contra
                la cuenta bancaria Alegra asociada a esta cartola, categorizado a la cuenta elegida, y el movimiento quedará conciliado.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEgDialog({ open: false, cargo: null })}>Cancelar</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" disabled={creando || !cuentaSel || !provSel}
              onClick={confirmarEgreso} data-testid="btn-confirmar-egreso-express">
              {creando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
              Crear egreso y conciliar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
